import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import { CultivarAreaForm, OrchardMap } from '../../components/orchard';
import PlantingPlanFields from '../../components/orchard/PlantingPlanFields';
import { draftCanLayOut, draftToSave, emptyPlanDraft, type PlanDraft } from '../../components/orchard/plantingPlanDraft';
import { Sprout, CheckCircle, Rows3, Shapes } from 'lucide-react';
import orchardService from '../../services/orchard';
import { cn } from '../../utils/cn';
import type { Block, CultivarArea, Farm, GeoJSONPolygon, PlantingPlan } from '../../types/orchard';

function CultivarAreaCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const blockIdParam = searchParams.get('block_id');
  const farmIdParam = searchParams.get('farm_id');
  const blockId = Number(blockIdParam);
  const farmId = Number(farmIdParam);
  const orgIdParam = searchParams.get('org_id');
  const targetOrgId = orgIdParam ? parseInt(orgIdParam, 10) : undefined;

  const [block, setBlock] = useState<Block | null>(null);
  const [farm, setFarm] = useState<Farm | null>(null);
  const [polygon, setPolygon] = useState<GeoJSONPolygon | null>(null);
  const [areaFromMap, setAreaFromMap] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successId, setSuccessId] = useState<number | null>(null);

  // How the planting is described. By rows, the boundary is drawn first and
  // then filled with rows — direction, spacing, which cultivar is in which
  // row — and the tree count comes from the rows instead of a spacing guess.
  const [plantedBy, setPlantedBy] = useState<'estimate' | 'rows'>('estimate');
  const [planDraft, setPlanDraft] = useState<PlanDraft>(emptyPlanDraft);
  const [planPreview, setPlanPreview] = useState<PlantingPlan | null>(null);
  const [drawingFirstRow, setDrawingFirstRow] = useState(false);
  const [mainCultivar, setMainCultivar] = useState<{ id: number | null; name: string | null }>({ id: null, name: null });
  const handleCultivarChange = useCallback(
    (id: number | null, name: string | null) => setMainCultivar({ id, name }),
    []
  );

  useEffect(() => {
    const loadParents = async () => {
      try {
        const promises = [];
        if (blockId) promises.push(orchardService.getBlock(blockId));
        if (farmId) promises.push(orchardService.getFarm(farmId));

        const [blockResponse, farmResponse] = await Promise.all(
          blockId && farmId
            ? [orchardService.getBlock(blockId, targetOrgId), orchardService.getFarm(farmId, targetOrgId)]
            : blockId
            ? [orchardService.getBlock(blockId, targetOrgId), Promise.resolve(null)]
            : [Promise.resolve(null), farmId ? orchardService.getFarm(farmId, targetOrgId) : Promise.resolve(null)]
        );

        if (blockResponse && 'success' in blockResponse && blockResponse.success && blockResponse.data) {
          setBlock(blockResponse.data as Block);
        }
        if (farmResponse && 'success' in farmResponse && farmResponse.success && farmResponse.data) {
          setFarm(farmResponse.data as Farm);
        }
      } catch {
        // Non-critical; form still works without parent context
      }
    };

    loadParents();
  }, [blockId, farmId, targetOrgId]);

  const handlePolygonCreated = useCallback(async (newPolygon: GeoJSONPolygon) => {
    setPolygon(newPolygon);
    try {
      const response = await orchardService.calculateArea(newPolygon);
      if (response.success && response.data) {
        setAreaFromMap(response.data.hectares);
      }
    } catch {
      setAreaFromMap(null);
    }
  }, []);

  const handlePolygonEdited = useCallback(async (editedPolygon: GeoJSONPolygon) => {
    setPolygon(editedPolygon);
    try {
      const response = await orchardService.calculateArea(editedPolygon);
      if (response.success && response.data) {
        setAreaFromMap(response.data.hectares);
      }
    } catch {
      setAreaFromMap(null);
    }
  }, []);

  const handleSubmit = async (fieldData: {
    name: string;
    cultivar_id: number;
    area_hectares?: number;
    tree_count?: number;
    row_spacing_meters?: number;
    tree_spacing_meters?: number;
    planting_date?: string;
    rootstock?: string;
    rootstock_cultivar_id?: number | null;
    interstem_cultivar_id?: number | null;
    graft_union_height_mm?: number | null;
    irrigation_zone?: string;
    training_system?: string;
    tree_supplier?: string;
    supplied_by_nursery_org_id?: number | null;
    pre_existing_supply?: {
      nursery_org_id?: number | null;
      supplier_name?: string;
      planting_year?: number | null;
    } | null;
    notes?: string;
    active?: boolean;
  }) => {
    if (!polygon || !blockId || !farmId) return;
    const byRows = plantedBy === 'rows';
    if (byRows && !draftCanLayOut(planDraft)) {
      setError('Draw row 1 on the map and set the row spacing — or choose "One cultivar" to estimate from spacing.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const centerPoint = orchardService.calculateCenterPoint(polygon.coordinates);

    try {
      const response = await orchardService.createCultivarArea({
        block_id: blockId,
        farm_id: farmId,
        name: fieldData.name,
        cultivar_id: fieldData.cultivar_id,
        boundary_geometry: polygon,
        center_point: centerPoint,
        area_hectares: fieldData.area_hectares ?? areaFromMap ?? undefined,
        // By rows, the server lays the rows out and takes both from them.
        tree_count: byRows ? undefined : fieldData.tree_count,
        row_spacing_meters: byRows ? undefined : fieldData.row_spacing_meters,
        tree_spacing_meters: byRows ? undefined : fieldData.tree_spacing_meters,
        planting_plan: byRows ? draftToSave(planDraft) : null,
        planting_date: fieldData.planting_date,
        rootstock: fieldData.rootstock,
        rootstock_cultivar_id: fieldData.rootstock_cultivar_id ?? null,
        interstem_cultivar_id: fieldData.interstem_cultivar_id ?? null,
        graft_union_height_mm: fieldData.graft_union_height_mm ?? null,
        irrigation_zone: fieldData.irrigation_zone,
        training_system: fieldData.training_system,
        tree_supplier: fieldData.tree_supplier,
        supplied_by_nursery_org_id: fieldData.supplied_by_nursery_org_id ?? null,
        pre_existing_supply: fieldData.pre_existing_supply ?? null,
        notes: fieldData.notes,
        active: fieldData.active,
        target_org_id: targetOrgId,
      });

      if (response.success && response.data) {
        setSuccessId(response.data.id);
        const qs = targetOrgId ? `?org_id=${targetOrgId}` : '';
        setTimeout(() => {
          navigate(`/orchard/cultivar-areas/${response.data!.id}${qs}`);
        }, 1500);
      } else {
        setError((response as any).message || 'Failed to create cultivar area');
      }
    } catch (e: any) {
      // Surface the server's reason — a 403 here is usually a licence problem
      // (no active orchard planting licence, or tree count over the cap).
      setError(e?.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!blockIdParam || !farmIdParam || !blockId || !farmId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="New Cultivar Area"
          subtitle="A block must be selected"
          icon={<Sprout className="h-6 w-6 text-white" />}
          showBackButton
        />
        <div className="max-w-xl mx-auto px-4 py-16 text-center">
          <p className="text-neutral-600 mb-4">
            No block specified. Please navigate from a block's detail page.
          </p>
        </div>
      </div>
    );
  }

  if (successId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="New Cultivar Area"
          subtitle="Area created successfully"
          icon={<Sprout className="h-6 w-6 text-white" />}
          showBackButton
        />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <div className="h-16 w-16 rounded-full bg-success-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-success-600" />
              </div>
              <h2 className="text-2xl font-bold text-neutral-900 mb-2">Cultivar Area Created!</h2>
              <p className="text-neutral-600 mb-2">Redirecting to area details...</p>
              <p className="text-sm text-neutral-500">
                Rows can be changed later from <strong>Edit</strong> on the area.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Build map context: show farm boundary and block boundary
  const contextBlocks = block ? [block] : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="New Cultivar Area"
        subtitle={
          block && farm
            ? `Adding area to ${block.name} in ${farm.name}`
            : 'Draw a boundary on the map to define your cultivar area'
        }
        icon={<Sprout className="h-6 w-6 text-white" />}
        showBackButton
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-error-50 border border-error-200 rounded-lg p-4">
            <p className="text-error-700">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-base font-semibold text-neutral-900 mb-3">How is it planted?</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5" role="radiogroup">
                {([
                  { key: 'estimate', icon: Shapes, title: 'One cultivar', body: 'Draw the boundary; trees are estimated from spacing.' },
                  { key: 'rows', icon: Rows3, title: 'In rows', body: 'Draw the boundary, then row 1 — set cultivars and pollinators by row.' },
                ] as const).map(({ key, icon: Icon, title, body }) => (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={plantedBy === key}
                    onClick={() => {
                      setPlantedBy(key);
                      if (key === 'estimate') setDrawingFirstRow(false);
                    }}
                    className={cn(
                      'text-left rounded-lg border p-3 transition-colors',
                      plantedBy === key
                        ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                        : 'border-neutral-200 hover:border-neutral-300'
                    )}
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
                      <Icon className="h-4 w-4 flex-shrink-0" /> {title}
                    </span>
                    <span className="block text-xs text-neutral-500 mt-1">{body}</span>
                  </button>
                ))}
              </div>

              <h2 className="text-base font-semibold text-neutral-900 mb-4">Cultivar Area Details</h2>
              <CultivarAreaForm
                areaFromMap={areaFromMap}
                hasPolygon={!!polygon}
                plantedBy={plantedBy}
                rowTreeCount={planPreview?.plan.total_trees ?? null}
                onCultivarChange={handleCultivarChange}
                onSubmit={handleSubmit}
                onCancel={() => navigate(blockId ? `/orchard/blocks/${blockId}` : '/orchard')}
                isSubmitting={isSubmitting}
              />
            </CardContent>
          </Card>

          {/* Map */}
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              <p className="text-sm text-amber-800 font-medium">
                {drawingFirstRow
                  ? 'Tap two points along row 1. Press Esc to start the line again.'
                  : polygon && plantedBy === 'rows'
                  ? 'Boundary drawn. Now draw row 1 from the Rows panel below the form.'
                  : 'Green = farm boundary, Blue = block boundary. Click the map to add boundary points, then click ✓ Finish (top-right) to complete the cultivar area.'}
              </p>
            </div>
            <div className="rounded-xl overflow-hidden border border-neutral-200 shadow-sm" style={{ height: '560px' }}>
              <OrchardMap
                farmBoundary={farm?.boundary_geometry}
                blocks={contextBlocks}
                drawMode={drawingFirstRow ? 'first-row-line' : 'cultivar-area'}
                onPolygonCreated={handlePolygonCreated}
                onPolygonEdited={handlePolygonEdited}
                // The draw tool drops its own copy of the boundary whenever it
                // unmounts (drawing row 1 swaps it out), so keep it on the map
                // independently.
                cultivarAreas={polygon ? [{ id: -1, name: 'New planting', boundary_geometry: polygon } as CultivarArea] : []}
                firstRowLine={
                  plantedBy === 'rows' && planDraft.first_row_start_point && planDraft.first_row_end_point
                    ? { start: planDraft.first_row_start_point, end: planDraft.first_row_end_point }
                    : undefined
                }
                onFirstRowLineCreated={(line) => {
                  setPlanDraft((d) => ({ ...d, first_row_start_point: line.start, first_row_end_point: line.end }));
                  setDrawingFirstRow(false);
                }}
                generatedRows={plantedBy === 'rows' ? planPreview?.rows : undefined}
                center={
                  block?.center_point
                    ? [block.center_point.lat, block.center_point.lng]
                    : farm?.center_point
                    ? [farm.center_point.lat, farm.center_point.lng]
                    : undefined
                }
                zoom={16}
              />
            </div>
            {polygon && (
              <p className="text-sm text-success-700 font-medium">
                ✓ Area boundary drawn{areaFromMap ? ` · ${areaFromMap.toFixed(2)} ha` : ''}
              </p>
            )}
          </div>
        </div>

        {plantedBy === 'rows' && (
          <div className="mt-6">
            <h2 className="text-lg font-semibold text-neutral-900 mb-3">Rows</h2>
            <PlantingPlanFields
              draft={planDraft}
              onChange={setPlanDraft}
              boundary={polygon}
              cultivarId={mainCultivar.id}
              cultivarName={mainCultivar.name}
              drawingFirstRow={drawingFirstRow}
              onRequestDrawFirstRow={() => setDrawingFirstRow(true)}
              onCancelDrawFirstRow={() => setDrawingFirstRow(false)}
              onPreview={setPlanPreview}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default CultivarAreaCreate;
